import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import type { DurationRange, SortKey } from "@/types/video";
import { filterByDuration, getCategoryBySlug, getVideosByCategory, sortVideos } from "@/utils/video";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/feature/Pagination";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";

const PAGE_SIZE = 4;

export default function Category() {
  const { categoryName = "" } = useParams();
  const category = getCategoryBySlug(categoryName);

  const [sort, setSort] = useState<SortKey>("newest");
  const [duration, setDuration] = useState<DurationRange>("all");
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState<"paged" | "more">("paged");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const listRef = useRef<HTMLDivElement>(null);

  useSEO({
    title: category ? `分类 - ${category.name} - 糖豆视频` : "分类未找到 - 糖豆视频",
    description: category
      ? `${category.name}分类：${category.description} 在糖豆视频浏览${category.name}类短视频，支持按时间、播放量排序与时长筛选。`
      : "未找到对应的视频分类，返回糖豆视频首页继续浏览。",
    keywords: category ? `${category.name}视频,${category.name}短视频,糖豆视频,视频分类` : "糖豆视频,视频分类",
  });

  const filtered = useMemo(() => {
    if (!category) return [];
    return filterByDuration(sortVideos(getVideosByCategory(categoryName), sort), duration);
  }, [category, categoryName, sort, duration]);

  useEffect(() => {
    setPage(1);
    setVisible(PAGE_SIZE);
  }, [categoryName, sort, duration]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const moreItems = filtered.slice(0, visible);
  const items = mode === "paged" ? pagedItems : moreItems;

  const scrollToList = () => {
    if (listRef.current) {
      const top = listRef.current.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handlePage = (p: number) => {
    setPage(p);
    scrollToList();
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="没有找到这个分类"
          description="该分类可能已下线或链接有误，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 pt-8 pb-6 bg-background-100 border-b border-background-200">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "分类" }, { label: category.name }]} />
        <div className="mt-4 flex items-center gap-3">
          <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-2xl">
            <i className={category.icon}></i>
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {category.name}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">{category.description}</p>
          </div>
        </div>
        <p className="mt-4 text-sm text-foreground-500">
          共 <span className="text-primary-700 font-medium">{filtered.length}</span> 个视频
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-8" ref={listRef}>
        <div className="flex flex-col gap-4 mb-8">
          <FilterBar
            sort={sort}
            onSortChange={setSort}
            duration={duration}
            onDurationChange={setDuration}
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500 mr-1 flex items-center gap-1">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-layout-grid-line"></i>
              </span>
              浏览方式
            </span>
            <div className="inline-flex items-center p-1 rounded-full bg-background-100 border border-background-200">
              <button
                type="button"
                onClick={() => setMode("paged")}
                className={`px-3.5 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  mode === "paged" ? "bg-accent-500 text-background-50 font-medium" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                翻页
              </button>
              <button
                type="button"
                onClick={() => setMode("more")}
                className={`px-3.5 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  mode === "more" ? "bg-accent-500 text-background-50 font-medium" : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                加载更多
              </button>
            </div>
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon="ri-filter-off-line"
            title="没有符合筛选条件的视频"
            description="试着放宽时长范围或切换排序方式，也可以看看该分类的全部视频。"
          />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-x-6 md:gap-y-8">
            {items.map((video) => (
              <VideoCard key={video.id} video={video} showDate showCategory />
            ))}
          </div>
        )}

        {mode === "paged" ? (
          <div className="mt-10">
            <Pagination page={page} totalPages={totalPages} onChange={handlePage} />
          </div>
        ) : (
          <div className="mt-10 flex justify-center">
            {visible < filtered.length ? (
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line"></i>
                </span>
                加载更多
              </button>
            ) : (
              <p className="text-sm text-foreground-400">已经看完全部分类视频啦</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}